import { Injectable } from '@angular/core';
import { MILL_SECONDS } from 'common/constant/general';
import { Compression } from 'common/utility/compression/compression';
import { Subject, filter, takeUntil } from 'rxjs';
import { NotificationStatus } from 'services/socket/classes/notification-status';
import { ResponseMessageTypes } from 'services/socket/classes/response-message-type';
import { SocketService } from 'services/socket/socket.service';
import { TokenService } from 'services/token/token.service';
import { Logger } from 'src/app/common/utility/logger/logger';
import { SocketMethod } from "src/app/services/socket/classes/socket-method";
import { Strings } from '../../common/utility/string/string';
import { GetDataRequestResult, GetDataRequestStatus, QueryRequest } from './get-data-common';

// Constants
const MAX_QUERY_TIME = 25 * (60 * 60 * MILL_SECONDS);
const QUERY_TIMEOUT = 120 * MILL_SECONDS; // 120 Seconds
const MAX_QUERY_RETRY = 3;

interface ServerTransaction {
  requesterTransactionId: string,
  request: QueryRequest,
  pageCount: number,
  startTime: number,
  queryRetry: number,
  buffer: Array<string>
}

interface ServerTransactionList {
  [key: string]: ServerTransaction
}

interface ClientTransactionList {
  [key: string]: { serverTranactionIds: Array<string> }
}


@Injectable({
  providedIn: 'root'
})
export class GetDataService {
  private unsubscribe: Subject<void> = new Subject();
  private serverTransactions: ServerTransactionList = {};
  private clientTransactions: ClientTransactionList = {};

  constructor(
    private tokenService: TokenService,
    private socketService: SocketService
  ) {
    this.listenForGetDataRequests();
  }

  private startTransactionTimeoutCheck(): void {
    const intid = setInterval(() => {
      const curTime = Date.now();

      Object.keys(this.serverTransactions).forEach(transId => {
        if ((curTime - this.serverTransactions[transId].startTime) > QUERY_TIMEOUT) {
          Logger.debug(`Query Timeout: ${transId} ${JSON.stringify(this.serverTransactions[transId].request)}`);
          if (this.serverTransactions[transId].queryRetry <= MAX_QUERY_RETRY) {
            this.sendServerRequest(
              this.serverTransactions[transId].requesterTransactionId,
              this.serverTransactions[transId].request,
              ++this.serverTransactions[transId].queryRetry
            );
          } else {
            const errorMsg = `Transaction failed to complete: ${transId}`;
            Logger.error(errorMsg);
            this.getDataUpdateSubject.next(
              this.createGetDataRequestResult(GetDataRequestStatus.Failed, this.serverTransactions[transId].requesterTransactionId, errorMsg));
          }
          // Delete the old transaction
          this.deleteTransaction(this.serverTransactions[transId].requesterTransactionId, transId);
        }
      });

      if (!Object.keys(this.serverTransactions).length) {
        clearInterval(intid);
      }
    }, MILL_SECONDS);
  } // startTransactionTimeoutCheck()

  private getDataUpdateSubject: Subject<GetDataRequestResult> = new Subject();
  public get getDataUpdated(): Subject<GetDataRequestResult> {
    return this.getDataUpdateSubject;
  } // get dashboardUpdated

  private createGetDataRequestResult(status: GetDataRequestStatus, transactionId: string, data?: any): GetDataRequestResult {
    const result: GetDataRequestResult = {
      status: status,
      transactionId: transactionId,
      data: data
    }
    return result;
  } // createGetDataRequestResult()


  private listenForGetDataRequests(): void {
    this.socketService.Messages.pipe(takeUntil(this.unsubscribe),
      filter((message: any) => 'messageType' in message)).
      subscribe((message: any) => {
        if (message.messageType === ResponseMessageTypes.GetData) {
          try {
            this.processGetData(message);
          } catch (error) {
            Logger.error(`processGetData: ${error} ${message}`)
          }
        }
      }); // sockerService Listener
  } // listenForGetDataRequests()

  public cancelGetData(transId: string): void {
    // Cancel all the pending server requests
    if (this.clientTransactions && this.clientTransactions[transId]) {
      this.clientTransactions[transId].serverTranactionIds.forEach(id => {
        delete this.serverTransactions[id];
      });
      delete this.clientTransactions[transId];
    }
  } // cancelGetData()

  public getData(query: QueryRequest): string | null {
    // Check for bad inputs
    if (query.startTime > query.endTime || query.startTime <= 0 || query.endTime <= 0) {
      Logger.error(`getData - Invalid start/end date/time ${JSON.stringify(query)}`)
      return null;
    }

    const requesterTransactionId = Strings.newGuid();
    let st = query.startTime;
    let et = 0;
    // Breakup the query into XX Hour chunks
    do {
      if ((st + MAX_QUERY_TIME) > query.endTime) {
        et = query.endTime;
      } else {
        et = st + MAX_QUERY_TIME;
      }

      const srvQuery: QueryRequest = {
        requests: query.requests,
        startTime: st,
        endTime: et,
        sort: query.sort
      }
      if (!this.sendServerRequest(requesterTransactionId, srvQuery, 0)) {
        return null;
      }

      st = et + 1;
    } while (et < query.endTime);

    this.startTransactionTimeoutCheck();

    return requesterTransactionId;
  } // getData()

  private sendServerRequest(requesterTransactionId: string, qry: QueryRequest, retry: number): boolean {

    let request;
    try {
      request = this.socketService.createRequest(SocketMethod.GetData, this.tokenService.AuthenticationId, qry, true);
      this.socketService.sendMessage(request);
      if (request.transactionId === undefined) {
        Logger.error("TransactionId from request was undefined");
        return false;
      }
    } catch (error) {
      Logger.error("Error creating request")
      return false;
    }

    const transaction: ServerTransaction = {
      requesterTransactionId: requesterTransactionId,
      request: qry,
      pageCount: 0,
      startTime: Date.now(),
      queryRetry: retry,
      buffer: []
    };

    // Update the transaction lists
    this.serverTransactions[request.transactionId] = transaction;
    if (!this.clientTransactions[requesterTransactionId])
      this.clientTransactions[requesterTransactionId] = { serverTranactionIds: [] };
    this.clientTransactions[requesterTransactionId].serverTranactionIds.push(request.transactionId);

    return true;
  } // sendServerRequest()

  private processGetData(message: any) {
    if (this.serverTransactions[message.transactionId] == undefined) {
      return;
    }
    let errorMsg = ''
    // Did response fail?
    if (message.data.status !== NotificationStatus.Success) {
      errorMsg = `getData API Failed: ${JSON.stringify(message)}`
      Logger.error(errorMsg)
      this.getDataUpdateSubject.next(
        this.createGetDataRequestResult(GetDataRequestStatus.Failed, message.transactionId, errorMsg));
      delete this.serverTransactions[message.transactionId]
      return;
    }
    const result = JSON.parse(message.data.message)

    // TODO:
    // - Add Error Handling, Timeout, etc.
    const clientId = this.serverTransactions[message.transactionId].requesterTransactionId;
    if (result.page !== result.numPages) {
      // If the buffer array hasn't been allocated, solution to out of sequence
      if (!this.serverTransactions[message.transactionId].buffer.length) {
        this.serverTransactions[message.transactionId].buffer = new Array(result.numPages)
        this.serverTransactions[message.transactionId].pageCount = 0;
      }

      this.serverTransactions[message.transactionId].buffer[result.page] = result.items
      this.serverTransactions[message.transactionId].pageCount++;
    } else {
      // Last page received from server for this transaction
      this.serverTransactions[message.transactionId].buffer[result.page] = result.items

      // Are any pages missing?
      if (this.serverTransactions[message.transactionId].pageCount != result.numPages) {
        Logger.error(`Pages Missing Mismatch: ${this.serverTransactions[message.transactionId].pageCount} Result: ${JSON.stringify(result)}`);

        if (this.serverTransactions[message.transactionId].queryRetry < MAX_QUERY_RETRY) {
          this.sendServerRequest(
            this.serverTransactions[message.transactionId].requesterTransactionId,
            this.serverTransactions[message.transactionId].request,
            this.serverTransactions[message.transactionId].queryRetry++
          );
        } else {
          // Transaction Failed to complete after XX Retries
          const errorMsg = `Tranaction failed to complete: ${message.transactionId}`;
          Logger.error(errorMsg);
          this.getDataUpdateSubject.next(
            this.createGetDataRequestResult(GetDataRequestStatus.Failed, message.transactionId, errorMsg));
        }
        // Always remove the old one that failed
        this.deleteTransaction(clientId, message.transactionId);
        return;
      }

      // Assemble the data
      let data: Array<any> = [];
      let buffer: string = '';
      this.serverTransactions[message.transactionId].buffer.forEach(buf => {
        buffer += buf
      });

      const tmp = Compression.decompress(JSON.parse(buffer).data);
      if (tmp === null)
        data = [];
      else
        data = JSON.parse(tmp);

      this.serverTransactions[message.transactionId].buffer.splice(0);

      // Send Data to Subscriber
      this.getDataUpdateSubject.next(
        this.createGetDataRequestResult(
          (this.clientTransactions[clientId].serverTranactionIds.length - 1) ? GetDataRequestStatus.NextPage : GetDataRequestStatus.Complete,
          this.serverTransactions[message.transactionId].requesterTransactionId, structuredClone(data)
        ));
      data.splice(0)

      this.deleteTransaction(clientId, message.transactionId);
    } // Last page received from server for this transaction

  } // processGetData()

  private deleteTransaction(clientId: string, transId: string): void {
    // Cleanup the Transaction Lists
    const ndx = this.clientTransactions[clientId].serverTranactionIds.indexOf(transId, 0);
    this.clientTransactions[clientId].serverTranactionIds.splice(ndx, 1);

    if (!this.clientTransactions[clientId].serverTranactionIds.length)
      delete this.clientTransactions[clientId];

    delete this.serverTransactions[transId];
  } // deleteTransaction()
} // class GetDataService

